import React, { useMemo, useState } from "react";
import { FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { Link } from "react-router-dom";

import { createShopOrder } from "../api";
import { useCart } from "../cart";
import { buildFallbackLabel, formatPrice } from "../utils";
import {
  STORE_CATALOG_PATH,
  STORE_HOME_PATH,
} from "../../routes";


const initialForm = {
  full_name: "",
  phone: "",
  address: "",
  comment: "",
};


export default function CartPage() {
  const { items, updateQuantity, removeItem, clearCart } = useCart();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0),
    [items]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!items.length) {
      return;
    }

    setSubmitting(true);
    setErrors({});
    setSuccessMessage("");

    try {
      await createShopOrder({
        ...form,
        items: items.map((item) => ({
          product: item.id,
          quantity: item.quantity,
        })),
      });

      clearCart();
      setForm(initialForm);
      setSuccessMessage("Muvaffaqiyatli rasmiylashtirdingiz.");
    } catch (error) {
      setErrors(error?.response?.data || {});
    } finally {
      setSubmitting(false);
    }
  };

  if (successMessage) {
    return (
      <main className="store-page">
        <div className="store-empty">
          <strong className="store-success-text">{successMessage}</strong>
          <Link to={STORE_HOME_PATH}>Bozorga qaytish</Link>
        </div>
      </main>
    );
  }

  if (!items.length) {
    return (
      <main className="store-page">
        <div className="store-empty">
          Savat bo'sh. <Link to={STORE_CATALOG_PATH}>Katalogga o'ting</Link>.
        </div>
      </main>
    );
  }

  return (
    <main className="store-page">
      <section className="store-cart-layout store-reveal">
        <div className="store-cart-items">
          <div className="store-section-head">
            <div>
              <p className="store-section-kicker">Savat</p>
              <h2>{items.length} ta mahsulot tanlangan</h2>
            </div>
            <Link className="store-text-link" to={STORE_CATALOG_PATH}>
              Yana mahsulot qo'shish
            </Link>
          </div>

          <div className="store-cart-list">
            {items.map((item) => (
              <article className="store-cart-item" key={item.id}>
                <div className="store-cart-item-media">
                  {item.primary_image ? (
                    <img src={item.primary_image} alt={item.name} />
                  ) : (
                    <div className="store-cart-item-fallback">{buildFallbackLabel(item.name)}</div>
                  )}
                </div>

                <div className="store-cart-item-copy">
                  <strong>{item.name}</strong>
                  <span>{item.sku || "SKU yo'q"}</span>
                  <small>{formatPrice(item.price)}</small>
                </div>

                <div className="store-cart-item-controls">
                  <button
                    aria-label="Soni kamaytirish"
                    className="store-cart-qty-button"
                    onClick={() => updateQuantity(item.id, Number(item.quantity || 1) - 1)}
                    title="Soni kamaytirish"
                    type="button"
                  >
                    <FiMinus aria-hidden="true" />
                  </button>

                  <span className="store-cart-qty-value" aria-label={`Soni: ${item.quantity}`}>
                    {item.quantity}
                  </span>

                  <button
                    aria-label="Soni oshirish"
                    className="store-cart-qty-button"
                    onClick={() => updateQuantity(item.id, Number(item.quantity || 1) + 1)}
                    title="Soni oshirish"
                    type="button"
                  >
                    <FiPlus aria-hidden="true" />
                  </button>

                  <button
                    aria-label="Olib tashlash"
                    className="store-cart-remove-button"
                    onClick={() => removeItem(item.id)}
                    title="Olib tashlash"
                    type="button"
                  >
                    <FiTrash2 aria-hidden="true" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>

        <aside className="store-cart-summary">
          <div className="store-order-head">
            <p className="store-section-kicker">Checkout</p>
            <h2>Buyurtma ma'lumotlarini kiriting.</h2>
            <div className="store-order-price">
              <strong>{formatPrice(totalAmount)}</strong>
            </div>
          </div>

          <form className="store-order-form" onSubmit={handleSubmit}>
            <label className="store-field">
              <span>Ism</span>
              <input
                onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))}
                value={form.full_name}
              />
              {errors.full_name ? <small>{errors.full_name}</small> : null}
            </label>

            <label className="store-field">
              <span>Telefon raqami</span>
              <input
                onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                value={form.phone}
              />
              {errors.phone ? <small>{errors.phone}</small> : null}
            </label>

            <label className="store-field">
              <span>Manzil</span>
              <input
                onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))}
                value={form.address}
              />
              {errors.address ? <small>{errors.address}</small> : null}
            </label>

            <label className="store-field">
              <span>Izoh</span>
              <textarea
                onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
                value={form.comment}
              />
              {errors.comment ? <small>{errors.comment}</small> : null}
            </label>

            <button className="store-link-button store-full-button" disabled={submitting} type="submit">
              {submitting ? "Yuborilmoqda..." : "Buyurtma qilish"}
            </button>

            {successMessage ? <p className="store-success-text">{successMessage}</p> : null}
          </form>
        </aside>
      </section>
    </main>
  );
}
